import { createFileRoute, Link } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { queryOptions } from "@tanstack/react-query";
import {
  Rocket,
  BadgeCheck,
  Trophy,
  Sparkles,
  Share2,
  Wallet,
  ShieldCheck,
  Globe2,
  ArrowRight,
  Timer,
  Percent,
  Scale,
  FileText,
  Gavel,
} from "lucide-react";
import { AnnouncementBar } from "@/components/AnnouncementBar";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { getReferralPublicSettings } from "@/lib/referral/referral.functions";

const CANONICAL = "https://fixhen.com/referral";
const OG_IMAGE = "https://fixhen.com/og/referral.jpg";

const settingsQuery = queryOptions({
  queryKey: ["referral", "public-settings"],
  queryFn: () => getReferralPublicSettings(),
  staleTime: 60_000,
});

export const Route = createFileRoute("/referral/")({
  loader: ({ context }) => context.queryClient.ensureQueryData(settingsQuery),
  head: () => ({
    meta: [
      { title: "FIXHEN Partner Program — Earn up to 15% per referral" },
      {
        name: "description",
        content:
          "Join the FIXHEN Partner Program and earn 10–15% commission on classic Mercedes-Benz and BMW parts you refer. 90-day cookie, three tiers, monthly payouts.",
      },
      { property: "og:title", content: "FIXHEN Partner Program — Earn up to 15%" },
      {
        property: "og:description",
        content:
          "Refer classic-car enthusiasts to FIXHEN and earn recurring commission on parts they order. Silver 10% · Gold 12.5% · Platinum 15%.",
      },
      { property: "og:url", content: CANONICAL },
      { property: "og:image", content: OG_IMAGE },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: OG_IMAGE },
    ],
    links: [{ rel: "canonical", href: CANONICAL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          serviceType: "Affiliate Partner Program",
          name: "FIXHEN Partner Program",
          provider: { "@type": "Organization", name: "FIXHEN", url: "https://fixhen.com/" },
          areaServed: "Worldwide",
          description:
            "Referral program paying 10–15% commission on classic Mercedes-Benz and BMW parts, with three performance tiers.",
          url: CANONICAL,
        }),
      },
    ],
  }),
  component: ReferralLandingPage,
});

const bp = (v: number) => (v / 100).toFixed(v % 100 === 0 ? 0 : 1) + "%";
const eur = (cents: number) =>
  new Intl.NumberFormat("en-EU", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(cents / 100);

function ReferralLandingPage() {
  const fetchSettings = useServerFn(getReferralPublicSettings);
  const { data: s } = useSuspenseQuery({
    ...settingsQuery,
    queryFn: () => fetchSettings(),
  });

  return (
    <div className="min-h-screen bg-paper text-ink">
      <AnnouncementBar />
      <SiteHeader />

      {/* HERO */}
      <section className="relative overflow-hidden bg-ink text-paper">
        <div
          className="absolute inset-0 opacity-40"
          style={{
            backgroundImage: "url(/og/referral.jpg)",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
          aria-hidden
        />
        <div
          className="absolute inset-0 bg-gradient-to-r from-ink via-ink/85 to-transparent"
          aria-hidden
        />
        <div className="relative mx-auto max-w-7xl px-4 py-24 sm:px-6 md:py-32">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-2 rounded-full border border-sky-pop/40 bg-sky-pop/10 px-3 py-1 text-xs font-bold uppercase tracking-widest text-sky-pop">
              <Sparkles className="h-3.5 w-3.5" /> FIXHEN Partner Program
            </span>
            <h1 className="mt-6 font-display text-4xl font-black leading-[1.02] sm:text-6xl md:text-7xl">
              Earn up to <span className="text-sky-pop">{bp(s.tier_platinum_bp)}</span> on every classic-car part you refer.
            </h1>
            <p className="mt-6 max-w-xl text-lg text-paper/80">
              Send enthusiasts our way and get a recurring commission on every order they place —
              for {s.cookie_days} days after their first click. Three performance tiers, monthly payouts, worldwide.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link
                to="/referral/apply"
                className="group inline-flex items-center gap-2 rounded-full bg-sky-pop px-8 py-4 text-base font-black text-ink shadow-lg shadow-sky-pop/25 transition-all hover:scale-105 hover:shadow-xl"
              >
                Apply to become a partner
                <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
              </Link>
              <a
                href="#how-it-works"
                className="text-sm font-semibold text-paper/80 underline-offset-4 hover:text-paper hover:underline"
              >
                How it works ↓
              </a>
            </div>
            <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-paper/70">
              <span className="flex items-center gap-2">
                <Timer className="h-4 w-4 text-sky-pop" /> {s.cookie_days}-day cookie
              </span>
              <span className="flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-sky-pop" /> {s.hold_days}-day verification
              </span>
              <span className="flex items-center gap-2">
                <Globe2 className="h-4 w-4 text-sky-pop" /> Worldwide
              </span>
              <span className="flex items-center gap-2">
                <Wallet className="h-4 w-4 text-sky-pop" /> Payouts from {eur(s.min_payout_cents)}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* TIER STAIRCASE */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6">
        <div className="mx-auto max-w-2xl text-center">
          <span className="text-xs font-bold uppercase tracking-widest text-sky-pop">The climb</span>
          <h2 className="mt-3 font-display text-3xl font-black sm:text-4xl">
            Every referral is a step higher
          </h2>
          <p className="mt-3 text-muted-foreground">
            Start at Silver the day you're approved. Your rate rises automatically as your lifetime
            referred revenue crosses each threshold — no negotiation, no waiting.
          </p>
        </div>

        {/* Staircase */}
        <div className="mt-16 grid gap-4 md:grid-cols-3 md:items-end md:gap-6">
          <TierStep
            name="Silver"
            rate={bp(s.tier_silver_bp)}
            unlock="Starting tier — the moment you're approved."
            step={1}
            heightClass="md:min-h-[280px]"
            surfaceClass="bg-gradient-to-br from-slate-100 to-white border-slate-200"
            numberClass="text-slate-400"
            icon={<BadgeCheck className="h-6 w-6" />}
          />
          <TierStep
            name="Gold"
            rate={bp(s.tier_gold_bp)}
            unlock={`Unlocks at ${eur(s.gold_threshold_cents)} in verified referred revenue.`}
            step={2}
            heightClass="md:min-h-[340px]"
            surfaceClass="bg-gradient-to-br from-amber-50 to-white border-amber-200"
            numberClass="text-amber-400"
            icon={<Trophy className="h-6 w-6" />}
          />
          <TierStep
            name="Platinum"
            rate={bp(s.tier_platinum_bp)}
            unlock={`Unlocks at ${eur(s.platinum_threshold_cents)} in verified referred revenue.`}
            step={3}
            heightClass="md:min-h-[400px]"
            surfaceClass="bg-gradient-to-br from-sky-100 to-white border-sky-300 ring-1 ring-sky-pop/40"
            numberClass="text-sky-pop"
            icon={<Rocket className="h-6 w-6" />}
            topBadge="Top tier"
          />
        </div>

        {/* Ascending progress bar with milestone markers */}
        <div className="mt-12 rounded-3xl border border-border bg-white p-6 shadow-sm sm:p-10">
          <div className="mb-6 flex items-center justify-between">
            <span className="text-sm font-bold uppercase tracking-widest text-muted-foreground">
              Your climb
            </span>
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-sky-pop">
              <ArrowRight className="h-3.5 w-3.5 -rotate-45" /> Keep going
            </span>
          </div>
          <div className="relative h-2.5 overflow-hidden rounded-full bg-slate-100">
            <div className="absolute inset-y-0 left-0 w-1/3 bg-slate-300" />
            <div className="absolute inset-y-0 left-1/3 w-1/3 bg-amber-300" />
            <div className="absolute inset-y-0 left-2/3 w-1/3 bg-sky-pop" />
            <div className="absolute left-1/3 top-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white bg-amber-400 shadow" />
            <div className="absolute left-2/3 top-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white bg-sky-pop shadow" />
          </div>
          <div className="mt-4 grid grid-cols-3 text-xs font-semibold">
            <div>
              <div className="text-ink">Silver · {bp(s.tier_silver_bp)}</div>
              <div className="text-muted-foreground">from €0</div>
            </div>
            <div className="text-center">
              <div className="text-ink">Gold · {bp(s.tier_gold_bp)}</div>
              <div className="text-muted-foreground">{eur(s.gold_threshold_cents)}+</div>
            </div>
            <div className="text-right">
              <div className="text-ink">Platinum · {bp(s.tier_platinum_bp)}</div>
              <div className="text-muted-foreground">{eur(s.platinum_threshold_cents)}+</div>
            </div>
          </div>
          <p className="mt-6 text-center text-xs text-muted-foreground">
            Tier upgrades apply to future orders. Every euro of verified revenue counts toward your next step.
          </p>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how-it-works" className="bg-slate-50 py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <div className="mx-auto max-w-2xl text-center">
            <span className="text-xs font-bold uppercase tracking-widest text-sky-pop">How it works</span>
            <h2 className="mt-3 font-display text-3xl font-black sm:text-4xl">Four steps to your first payout</h2>
          </div>
          <div className="mt-14 grid gap-8 md:grid-cols-4">
            <Step n={1} icon={<BadgeCheck className="h-6 w-6" />} title="Apply">
              Tell us who you are, where you'll promote FIXHEN, and how you'd like to be paid. Approvals are usually within 48 hours.
            </Step>
            <Step n={2} icon={<Share2 className="h-6 w-6" />} title="Get your link">
              We hand you a unique code and a branded referral link that works on every page — <span className="rounded bg-white px-1.5 py-0.5 font-mono text-[11px]">fixhen.com/?ref=YOURCODE</span>
            </Step>
            <Step n={3} icon={<Sparkles className="h-6 w-6" />} title="Share it">
              Post in your video description, blog, forum signature, newsletter. Every click is cookied for {s.cookie_days} days.
            </Step>
            <Step n={4} icon={<Wallet className="h-6 w-6" />} title="Get paid">
              Commissions clear after {s.hold_days} days. Request a payout once your balance passes {eur(s.min_payout_cents)}.
            </Step>
          </div>
        </div>
      </section>

      {/* WHY */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6">
        <div className="grid gap-12 md:grid-cols-2 md:items-center">
          <div>
            <span className="text-xs font-bold uppercase tracking-widest text-sky-pop">Why partner with FIXHEN</span>
            <h2 className="mt-3 font-display text-3xl font-black sm:text-4xl">
              Parts your audience actually wants — and can't find anywhere else.
            </h2>
            <ul className="mt-6 space-y-4 text-base text-slate-700">
              <li className="flex gap-3">
                <Percent className="mt-1 h-5 w-5 flex-none text-sky-pop" />
                <span>
                  <strong className="font-bold">Best-in-class commissions</strong> — {bp(s.tier_silver_bp)}–{bp(s.tier_platinum_bp)}, with a {s.cookie_days}-day cookie (the industry median is 30).
                </span>
              </li>
              <li className="flex gap-3">
                <ShieldCheck className="mt-1 h-5 w-5 flex-none text-sky-pop" />
                <span>
                  <strong className="font-bold">Transparent tracking</strong> — every click, order, and commission is visible in your dashboard, updated in real time.
                </span>
              </li>
              <li className="flex gap-3">
                <Globe2 className="mt-1 h-5 w-5 flex-none text-sky-pop" />
                <span>
                  <strong className="font-bold">Worldwide catalog</strong> — Mercedes-Benz R129/W124/W201/W208, BMW E30/E31/E34/E36/E46 and growing. Your audience finds parts they've been chasing for years.
                </span>
              </li>
              <li className="flex gap-3">
                <Rocket className="mt-1 h-5 w-5 flex-none text-sky-pop" />
                <span>
                  <strong className="font-bold">Tier promotions</strong> — cross a threshold and your rate goes up on every future order, not just the next one.
                </span>
              </li>
            </ul>
            <div className="mt-8">
              <Link
                to="/referral/apply"
                className="inline-flex items-center gap-2 rounded-full bg-ink px-6 py-3 text-sm font-bold text-paper transition-transform hover:scale-105"
              >
                Start earning today <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <StatCard label="Cookie window" value={`${s.cookie_days} days`} />
            <StatCard label="Hold period" value={`${s.hold_days} days`} />
            <StatCard label="Minimum payout" value={eur(s.min_payout_cents)} />
            <StatCard label="Top tier rate" value={bp(s.tier_platinum_bp)} />
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-slate-50 py-20">
        <div className="mx-auto max-w-3xl px-4 sm:px-6">
          <div className="text-center">
            <span className="text-xs font-bold uppercase tracking-widest text-sky-pop">FAQ</span>
            <h2 className="mt-3 font-display text-3xl font-black sm:text-4xl">Common questions</h2>
          </div>
          <Accordion type="single" collapsible className="mt-10">
            <AccordionItem value="q1">
              <AccordionTrigger>How exactly does the referral get tracked?</AccordionTrigger>
              <AccordionContent>
                Your unique link contains a <code>?ref=YOURCODE</code> parameter. When someone clicks it, we set a first-party cookie in their browser that lasts {s.cookie_days} days. Any order they place from that browser within the window is automatically attributed to you (last-click).
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="q2">
              <AccordionTrigger>What's the {s.hold_days}-day hold?</AccordionTrigger>
              <AccordionContent>
                A commission becomes "verified" {s.hold_days} calendar days after the order ships, provided the customer hasn't cancelled, returned, or charged back. This protects everyone — you, us, and the buyer.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="q3">
              <AccordionTrigger>How and when do I get paid?</AccordionTrigger>
              <AccordionContent>
                Once your verified balance is at least {eur(s.min_payout_cents)}, you can request a payout from your dashboard. We support bank transfer, PayPal, Wise and Revolut. A processing fee of {eur(s.payout_fee_cents)} is deducted from each payout.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="q4">
              <AccordionTrigger>How are the tier thresholds calculated?</AccordionTrigger>
              <AccordionContent>
                We use <strong>lifetime verified referred revenue</strong> — the sum of order subtotals (excluding VAT and shipping) that have made it past the hold period. Refunded orders don't count.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="q5">
              <AccordionTrigger>Can I refer myself?</AccordionTrigger>
              <AccordionContent>
                No. Self-referrals are blocked automatically and any commission generated by them is void. Please see the full terms.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="q6">
              <AccordionTrigger>Can I use paid ads?</AccordionTrigger>
              <AccordionContent>
                Yes, with restrictions: no bidding on "FIXHEN" or trademark variants, no misleading claims, no incentivized traffic without disclosure. Details in the terms.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="q7">
              <AccordionTrigger>Do I own the customer relationship?</AccordionTrigger>
              <AccordionContent>
                No — customers are FIXHEN's. We do not share end-customer personal data with partners. You get aggregated metrics only.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="q8">
              <AccordionTrigger>Do I need to be a business or can I apply as an individual?</AccordionTrigger>
              <AccordionContent>
                Both are welcome. If you're a registered business you'll invoice us for your commissions; individuals receive payouts and are responsible for declaring the income under local tax rules.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="q9">
              <AccordionTrigger>How long does approval take?</AccordionTrigger>
              <AccordionContent>
                Usually within 48 hours. We review the audience you described and your promo channels to keep the program brand-safe.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="q10">
              <AccordionTrigger>Where do I read the full terms?</AccordionTrigger>
              <AccordionContent>
                Right here:{" "}
                <Link to="/referral/terms" className="font-semibold text-sky-pop underline underline-offset-4">
                  FIXHEN Partner Program Terms
                </Link>
                . You accept the current version when you submit your application.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* FULL TERMS & CONDITIONS (BG) */}
      <ReferralTermsSection s={s} />

      {/* FOOTER CTA BAND */}
      <section className="bg-ink py-20 text-paper">
        <div className="mx-auto max-w-4xl px-4 text-center sm:px-6">
          <h2 className="font-display text-4xl font-black sm:text-5xl">Your first commission is a click away.</h2>
          <p className="mx-auto mt-4 max-w-xl text-paper/80">
            Approvals in 48 hours. No minimum audience. No monthly fee. Just a fair cut of every sale you drive.
          </p>
          <div className="mt-10">
            <Link
              to="/referral/apply"
              className="group inline-flex items-center gap-2 rounded-full bg-sky-pop px-10 py-5 text-base font-black text-ink shadow-xl transition-all hover:scale-105"
            >
              Apply now — it takes 3 minutes
              <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
          <p className="mt-6 text-xs text-paper/60">
            By applying you agree to the{" "}
            <Link to="/referral/terms" className="underline underline-offset-4 hover:text-paper">
              Partner Program Terms
            </Link>
            .
          </p>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}

function TierStep({
  name,
  rate,
  unlock,
  step,
  heightClass,
  surfaceClass,
  numberClass,
  icon,
  topBadge,
}: {
  name: string;
  rate: string;
  unlock: string;
  step: number;
  heightClass: string;
  surfaceClass: string;
  numberClass: string;
  icon: React.ReactNode;
  topBadge?: string;
}) {
  return (
    <div
      className={`relative flex flex-col overflow-hidden rounded-3xl border p-7 shadow-sm transition hover:shadow-md ${surfaceClass} ${heightClass}`}
    >
      <span
        className={`pointer-events-none absolute -right-2 -top-4 select-none font-display text-[7rem] font-black leading-none opacity-30 ${numberClass}`}
        aria-hidden
      >
        {step}
      </span>
      <div className="relative flex items-center justify-between">
        <div className="grid h-11 w-11 place-items-center rounded-2xl bg-ink text-paper">{icon}</div>
        {topBadge ? (
          <span className="rounded-full bg-ink px-3 py-1 text-[10px] font-black uppercase tracking-widest text-paper">
            {topBadge}
          </span>
        ) : null}
      </div>
      <div className="relative mt-auto pt-8">
        <h3 className="text-xl font-black text-ink">{name}</h3>
        <p className="mt-1 font-display text-5xl font-black tracking-tight text-ink">{rate}</p>
        <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{unlock}</p>
      </div>
    </div>
  );
}

function Step({
  n,
  icon,
  title,
  children,
}: {
  n: number;
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="relative rounded-2xl border border-border bg-white p-6">
      <span className="absolute -top-3 left-6 rounded-full bg-ink px-2.5 py-0.5 text-xs font-black text-paper">
        {String(n).padStart(2, "0")}
      </span>
      <div className="mb-4 text-sky-pop">{icon}</div>
      <h3 className="text-lg font-bold text-ink">{title}</h3>
      <p className="mt-2 text-sm text-slate-600">{children}</p>
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-2xl border border-border bg-white p-6">
      <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">{label}</p>
      <p className="mt-2 text-3xl font-black text-ink">{value}</p>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────── */
/* Full terms & conditions — lawyer-drafted, Bulgarian, dynamic values.   */
/* ────────────────────────────────────────────────────────────────────── */

const TERMS_TOC = [
  { id: "definitions",     label: "1. Definitions" },
  { id: "parties",         label: "2. Parties & scope" },
  { id: "eligibility",     label: "3. Eligibility" },
  { id: "application",     label: "4. Application & approval" },
  { id: "tracking",        label: "5. Tracking & attribution" },
  { id: "commission",      label: "6. Commissions & tiers" },
  { id: "hold",            label: "7. Hold period & qualification" },
  { id: "payout",          label: "8. Payouts" },
  { id: "tax",             label: "9. Taxes, VAT & invoicing" },
  { id: "prohibited",      label: "10. Prohibited practices" },
  { id: "advertising",     label: "11. Advertising & disclosure" },
  { id: "ip",              label: "12. Intellectual property & brand licence" },
  { id: "data",            label: "13. Data protection (GDPR)" },
  { id: "confidentiality", label: "14. Confidentiality" },
  { id: "changes",         label: "15. Changes to the program" },
  { id: "term",            label: "16. Term & termination" },
  { id: "warranties",      label: "17. Warranties & liability" },
  { id: "indemnity",       label: "18. Indemnity" },
  { id: "force",           label: "19. Force majeure" },
  { id: "assignment",      label: "20. Assignment, severability, entire agreement" },
  { id: "law",             label: "21. Governing law & jurisdiction" },
  { id: "contact",         label: "22. Contact" },
] as const;

const PROGRAM_EMAIL = "info@contact.fixhen.com";
const PROGRAM_PHONE = "+359 88 775 2171";
const PROGRAM_ADDRESS = "6 Prof. Dr Dimitar Dobrev St., Sofia, Bulgaria";
const PROGRAM_HOURS = "Mon–Fri, 09:00–18:00 EET";

function ReferralTermsSection({
  s,
}: {
  s: Awaited<ReturnType<typeof getReferralPublicSettings>>;
}) {
  const effectiveDate = new Date().toLocaleDateString("en-GB", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
  const pct = (v: number) =>
    new Intl.NumberFormat("en-GB", {
      minimumFractionDigits: v % 100 === 0 ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(v / 100) + "%";
  const euro = (cents: number) =>
    new Intl.NumberFormat("en-GB", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(cents / 100);

  return (
    <section id="terms" className="scroll-mt-20 bg-paper py-24">
      <div className="mx-auto max-w-5xl px-4 sm:px-6">
        {/* Header */}
        <div className="grid gap-8 border-b border-border pb-10 md:grid-cols-[minmax(0,1fr)_auto] md:items-end">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-3 py-1 text-[11px] font-bold uppercase tracking-[0.2em] text-slate-600">
              <Gavel className="h-3.5 w-3.5 text-sky-pop" /> Legal document
            </span>
            <h2 className="mt-4 font-display text-4xl font-black leading-tight text-ink sm:text-5xl">
              Partner Program Terms &amp; Conditions
            </h2>
            <p className="mt-3 max-w-2xl text-slate-600">
              These Terms &amp; Conditions (the "<strong>Terms</strong>") govern your participation
              in the <strong>FIXHEN</strong> Partner Program (the "<strong>Program</strong>").
              Please read them carefully — by submitting an application you confirm that
              you have read, understood and agreed to be bound by them.
            </p>
          </div>
          <div className="rounded-2xl border border-border bg-white p-5 text-sm shadow-sm">
            <div className="flex items-center gap-2 text-slate-500">
              <FileText className="h-4 w-4" /> <span className="text-xs font-bold uppercase tracking-widest">Version</span>
            </div>
            <div className="mt-1 font-display text-3xl font-black tabular-nums text-ink">v{s.terms_version}</div>
            <div className="mt-2 text-xs text-slate-500">In force from {effectiveDate}</div>
            <Link
              to="/referral/terms"
              className="mt-4 inline-flex items-center gap-1 text-xs font-semibold text-sky-pop hover:underline"
            >
              Standalone page <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
        </div>

        {/* Table of contents */}
        <nav
          aria-label="Table of contents"
          className="mt-10 rounded-2xl border border-border bg-white p-6"
        >
          <div className="mb-4 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
            <Scale className="h-3.5 w-3.5" /> Contents
          </div>
          <ol className="grid grid-cols-1 gap-x-8 gap-y-1.5 text-sm sm:grid-cols-2">
            {TERMS_TOC.map((c) => (
              <li key={c.id}>
                <a
                  href={`#${c.id}`}
                  className="block rounded-md px-2 py-1 text-slate-700 transition hover:bg-slate-50 hover:text-ink"
                >
                  {c.label}
                </a>
              </li>
            ))}
          </ol>
        </nav>

        {/* Body */}
        <article
          className="prose prose-slate mt-12 max-w-none
                     prose-headings:font-display prose-headings:font-black prose-headings:tracking-tight
                     prose-h3:mt-10 prose-h3:scroll-mt-24 prose-h3:text-xl
                     prose-p:text-slate-700 prose-li:text-slate-700
                     prose-strong:text-ink prose-a:text-sky-pop prose-a:no-underline hover:prose-a:underline"
        >
          <p className="lead text-base text-slate-700">
            These Terms constitute a continuing commercial agreement between FIXHEN and
            the approved Partner. They are drafted in accordance with Bulgarian law and
            applicable European Union law (including Regulation (EU) 2016/679 "GDPR",
            Regulation (EU) 2022/2065 "Digital Services Act" and Directive 2005/29/EC on
            unfair commercial practices) and with good faith commercial practice in
            online affiliate marketing. Section headings are for convenience only and
            do not affect interpretation.
          </p>

          <h3 id="definitions">1. Definitions</h3>
          <ul>
            <li><strong>"FIXHEN"</strong> — the trading entity operating the online store for classic Mercedes-Benz and BMW parts and administering the Program.</li>
            <li><strong>"Partner" / "you"</strong> — the natural person or legal entity whose application has been approved and to whom an active Referral Code has been issued.</li>
            <li><strong>"Referral Code / Link"</strong> — the unique alphanumeric identifier and/or URL containing the <code>?ref=</code> parameter used to track referred visits and orders.</li>
            <li><strong>"Cookie Window"</strong> — a period of <strong>{s.cookie_days} calendar days</strong> from the last click on the Referral Link, during which an order is attributed to the Partner.</li>
            <li><strong>"Qualified Sale"</strong> — an end-customer order for which, at checkout, a valid Partner cookie was active, the order reaches the "<em>shipped</em>" status and is not cancelled, returned, charged back or reversed within the Hold Period.</li>
            <li><strong>"Commission"</strong> — the amount payable to the Partner on a Qualified Sale, calculated per Section 6.</li>
            <li><strong>"Hold Period"</strong> — <strong>{s.hold_days} calendar days</strong> from the order's shipping date.</li>
            <li><strong>"Payout"</strong> — the transfer of a verified Commission per Section 8.</li>
            <li><strong>"Tier"</strong> — Silver, Gold or Platinum, determining the applicable rate under Section 6.</li>
          </ul>

          <h3 id="parties">2. Parties &amp; scope</h3>
          <p>
            These Terms bind FIXHEN and the Partner only. They confer no rights on third
            parties, save where expressly stated. In the event of any discrepancy between
            the English and any translated version, the English text prevails, save that
            Partners established in the Republic of Bulgaria may rely on a Bulgarian
            translation to the extent required by mandatory local law.
          </p>

          <h3 id="eligibility">3. Eligibility</h3>
          <ol>
            <li>The Partner must be at least <strong>18 years of age</strong> and have full legal capacity, or represent a duly registered legal entity in good standing.</li>
            <li>Current employees and permanent consultants of FIXHEN, and their spouses/partners and first-degree descendants, are not eligible.</li>
            <li>One legal entity may hold one active Partner account.</li>
            <li>FIXHEN reserves the right to reject any application at its sole discretion, without providing reasons, insofar as this does not breach mandatory law.</li>
          </ol>

          <h3 id="application">4. Application &amp; approval</h3>
          <p>
            Applications are submitted through the form at{" "}
            <Link to="/referral/apply">/referral/apply</Link>. By submitting the form the
            Partner represents that the information provided is true, complete and current,
            and that the Partner holds all necessary rights to promote through the declared
            channels. Applications are reviewed within <strong>48 business hours</strong>.
            The agreement is deemed concluded on activation of the Referral Code.
          </p>

          <h3 id="tracking">5. Tracking &amp; attribution</h3>
          <ol>
            <li>Attribution uses a first-party cookie <code>fh_ref</code> with a lifetime of {s.cookie_days} days on a <strong>last-click</strong> basis.</li>
            <li>If, within the window, an end-customer clicks another partner's link, attribution passes to that partner.</li>
            <li>FIXHEN accepts no responsibility for lost attribution resulting from: cookie blocking or deletion by browsers or extensions; shortening of cookie lifetime by browser policies; device changes; private/incognito browsing; or restrictions imposed by law.</li>
            <li>Server-to-server, cross-device and email-hash attribution are not supported unless expressly agreed in writing.</li>
            <li>Data shown in the Partner dashboard is indicative until final validation and constitutes sufficient evidence of Commissions due absent manifest technical error.</li>
          </ol>

          <h3 id="commission">6. Commissions &amp; tiers</h3>
          <p>Commission is calculated on the <strong>net order subtotal</strong> (excluding VAT, shipping, insurance and refunded amounts) at the following rates:</p>
          <ul>
            <li><strong>Silver — {pct(s.tier_silver_bp)}</strong> — starting tier on approval.</li>
            <li><strong>Gold — {pct(s.tier_gold_bp)}</strong> — unlocked at {euro(s.gold_threshold_cents)} lifetime verified referred revenue.</li>
            <li><strong>Platinum — {pct(s.tier_platinum_bp)}</strong> — unlocked at {euro(s.platinum_threshold_cents)} lifetime verified referred revenue.</li>
          </ul>
          <p>
            The applicable rate is <strong>snapshotted at the time of the order</strong>;
            subsequent tier promotions do not apply retroactively. Tier progress is
            calculated from verified revenue, net of returns, chargebacks and reversals.
            FIXHEN may, at its discretion, agree an individual rate (override) which is
            recorded in the Partner profile and prevails over the tier rate.
          </p>

          <h3 id="hold">7. Hold Period &amp; qualification</h3>
          <p>
            Each Commission is created with the status <em>"pending"</em> and becomes
            <em> "approved"</em> only after the Hold Period of {s.hold_days} days from the
            shipping date has elapsed, provided the order has not been cancelled,
            returned, charged back, deemed fraudulent or found to be in breach of these
            Terms. FIXHEN may reverse an approved Commission at any time on subsequent
            cancellation, refund, finding of fraud or breach; reversed amounts are
            deducted from tier progress.
          </p>

          <h3 id="payout">8. Payouts</h3>
          <ol>
            <li>Payouts are made in <strong>EUR</strong> once the verified balance reaches the minimum of <strong>{euro(s.min_payout_cents)}</strong>.</li>
            <li>The Partner initiates a Payout from the dashboard; FIXHEN processes valid requests within <strong>30 business days</strong>.</li>
            <li>A processing fee of <strong>{euro(s.payout_fee_cents)}</strong> is deducted per Payout to cover bank/PSP charges.</li>
            <li>Supported methods: bank/SEPA transfer, PayPal, Wise, Revolut — subject to availability and successful KYC.</li>
            <li>FIXHEN may delay a Payout to complete tax, KYC or anti-fraud checks, where breach is reasonably suspected, or on the order of a competent authority.</li>
            <li>The Partner is responsible for keeping payment details accurate and current. FIXHEN is not liable for failed transfers due to incorrect details; in such cases the amount remains on the Partner balance, less the cost of the failed attempt.</li>
          </ol>

          <h3 id="tax">9. Taxes, VAT &amp; invoicing</h3>
          <p>
            The Partner is solely responsible for declaring and paying all taxes, social
            contributions and duties due on Commissions received in the jurisdiction of
            their tax residence. Commissions are stated <strong>exclusive of VAT</strong>.
            Where the Partner is a VAT-registered business established in an EU Member
            State other than that of FIXHEN, the reverse-charge mechanism (art. 44 of
            Directive 2006/112/EC) applies. On request, the Partner shall provide a valid
            VIES number, tax registration certificate or equivalent document. Natural
            persons may be asked to provide a national tax identification number for
            regulatory reporting purposes.
          </p>

          <h3 id="prohibited">10. Prohibited practices</h3>
          <p>The Partner shall not, directly or through third parties:</p>
          <ul>
            <li>send unsolicited commercial communications ("spam") by email, SMS, messengers or social networks containing the Referral Link;</li>
            <li>bid in paid search (Google Ads, Bing Ads, etc.) on keywords containing "FIXHEN", their misspellings or the company's domains;</li>
            <li>purchase traffic from botnets, click-farms or incentivized-traffic sources;</li>
            <li>engage in cookie stuffing, hidden iframes, auto-redirects or any technique that artificially triggers the cookie;</li>
            <li>offer cashback, rebates, vouchers or monetary incentives to end-customers without FIXHEN's prior written consent;</li>
            <li>publish misleading, false or unsubstantiated claims regarding products, prices, delivery times or warranties;</li>
            <li>promote the Program on sites/channels containing adult, discriminatory, violence-inciting, illegal or manifestly misleading content;</li>
            <li>self-refer, or refer through household members or connected persons;</li>
            <li>register domains, trade names, usernames or social accounts containing "FIXHEN" or our marks without prior written consent.</li>
          </ul>
          <p>Breach of this Section entitles FIXHEN to withhold, refuse or reverse Commissions, terminate the agreement without notice and claim damages in full.</p>

          <h3 id="advertising">11. Advertising, consumer law &amp; disclosure</h3>
          <p>
            The Partner shall ensure that all marketing communications comply with
            applicable law, including consumer protection legislation, e-commerce rules,
            Directive 2005/29/EC, Regulation (EU) 2022/2065 (Digital Services Act) and
            the applicable guidance of national regulators. The Partner must clearly and
            plainly disclose the existence of an affiliate/remunerated relationship (e.g.
            "This is an affiliate link — I earn a commission on purchases.") in
            accordance with art. 26 DSA and the applicable platform guidelines (Meta,
            TikTok, YouTube, X and others).
          </p>

          <h3 id="ip">12. Intellectual property &amp; brand licence</h3>
          <p>
            Subject to compliance with these Terms, FIXHEN grants the Partner a limited,
            non-exclusive, non-transferable and revocable licence to use the "FIXHEN"
            name, logo and approved marketing creatives solely for the purpose of
            promotion within the Program. The Partner shall not: (a) modify the logo;
            (b) imply partnership, sponsorship or official representation beyond the
            scope of the Program; (c) use the mark for online advertising outside the
            channels declared in the application without express consent. All rights not
            expressly granted are reserved to FIXHEN. All goodwill arising from the
            Partner's use of the mark inures to FIXHEN.
          </p>

          <h3 id="data">13. Data protection (GDPR)</h3>
          <p>
            <strong>Controller</strong> of the Partner's personal data is FIXHEN. We
            process the following categories: identification data (first name, surname),
            contact data (email, phone), payment data (IBAN, PayPal ID and similar),
            country, languages, operational data (clicks, conversions, payouts) and IP
            data on system events.
          </p>
          <ul>
            <li><strong>Legal bases:</strong> performance of the contract (art. 6(1)(b) GDPR); compliance with legal obligations — accounting, tax, anti-fraud (art. 6(1)(c)); legitimate interest — security and fraud prevention (art. 6(1)(f)).</li>
            <li><strong>Retention:</strong> operational data — for the term of the agreement; accounting records — 10 years under the Bulgarian Accountancy Act; data from rejected applications — up to 6 months.</li>
            <li><strong>Recipients:</strong> payment institutions (SEPA/PayPal/Wise/Revolut), hosting provider, accounting provider, competent public authorities on lawful request.</li>
            <li><strong>Transfers outside the EEA:</strong> use of PayPal/Wise may involve transfers to the US and the UK on the basis of Standard Contractual Clauses under art. 46 GDPR.</li>
            <li><strong>Rights:</strong> access, rectification, erasure, restriction, portability, objection and complaint to the Bulgarian Commission for Personal Data Protection (<a href="https://www.cpdp.bg" target="_blank" rel="noopener noreferrer">www.cpdp.bg</a>).</li>
            <li><strong>Contact:</strong> <a href={`mailto:${PROGRAM_EMAIL}`}>{PROGRAM_EMAIL}</a>.</li>
          </ul>
          <p>
            <strong>Important:</strong> FIXHEN does not share end-customer personal data
            with the Partner; the Partner receives only aggregated metrics. The Partner
            shall not collect end-customer data in connection with promoting the Program.
          </p>

          <h3 id="confidentiality">14. Confidentiality</h3>
          <p>
            All information disclosed by FIXHEN to the Partner and marked as
            confidential, or which by its nature is confidential (including individual
            rates, commercial terms, roadmap, internal metrics), shall be treated as
            confidential. The confidentiality obligation survives termination of the
            agreement for a period of <strong>3 years</strong>. Information is not
            confidential if it: (i) is in the public domain other than through the
            recipient's fault; (ii) was known to the recipient prior to disclosure;
            (iii) is lawfully received from a third party without a duty of
            confidentiality; or (iv) is disclosed pursuant to law or court order.
          </p>

          <h3 id="changes">15. Changes to the program</h3>
          <p>
            FIXHEN may unilaterally amend these Terms, the Commission rates, tier
            thresholds, Hold Period, Cookie Window, minimum Payout amount, processing
            fee or any other operational parameter, on <strong>30 days' notice</strong>
            given by email or in-dashboard notification. Continued participation after
            the effective date constitutes acceptance. Rate changes are not applied
            retroactively to already-recorded orders. Changes required by mandatory
            legislation may take effect immediately.
          </p>

          <h3 id="term">16. Term &amp; termination</h3>
          <p>
            The agreement is of indefinite duration and takes effect on approval of the
            application. Either party may terminate for convenience on <strong>14 days'
            written notice</strong>. FIXHEN may terminate without notice for: material
            breach; fraud; insolvency/liquidation; reputational risk; prolonged
            inactivity (over 12 months without a Qualified Sale). Upon termination:
          </p>
          <ul>
            <li>the Referral Code is disabled immediately;</li>
            <li>verified Commissions above the Payout minimum are paid in the next cycle;</li>
            <li>Commissions in "pending" status are forfeited;</li>
            <li>any amounts owed to FIXHEN arising from reversals or breach remain due and payable.</li>
          </ul>

          <h3 id="warranties">17. Warranties &amp; liability</h3>
          <p>
            The Program is provided "as is" and "as available". To the maximum extent
            permitted by law FIXHEN does not warrant uninterrupted or error-free
            operation or any specific results. FIXHEN's aggregate liability arising out
            of or in connection with the Program, on any basis (contract, tort or
            otherwise), is limited to the total Commissions actually paid to the Partner
            in the <strong>twelve (12) months</strong> preceding the event giving rise
            to the claim. Nothing in these Terms excludes or limits liability that
            cannot be excluded under mandatory law (including wilful misconduct, gross
            negligence, or damage to life or health).
          </p>

          <h3 id="indemnity">18. Indemnity</h3>
          <p>
            The Partner shall indemnify, defend and hold harmless FIXHEN, its officers,
            employees and representatives from and against any third-party claims,
            actions, penalties and costs (including reasonable legal fees) arising out
            of: (a) the Partner's participation in the Program; (b) the content of the
            Partner's marketing communications; (c) the use of our marks in breach of
            the licence; (d) breach of applicable law, including consumer protection,
            data protection or competition law.
          </p>

          <h3 id="force">19. Force majeure</h3>
          <p>
            Neither party is liable for delay or non-performance caused by an event
            beyond its reasonable control, including natural disasters, war, terrorism,
            large-scale cyber-attack, failure of public telecommunications or payment
            infrastructure, epidemic, or restrictions imposed by a public authority. The
            affected party shall notify the other without delay and take reasonable
            steps to mitigate.
          </p>

          <h3 id="assignment">20. Assignment, severability, entire agreement</h3>
          <p>
            The Partner may not assign its rights or obligations under these Terms
            without FIXHEN's prior written consent. FIXHEN may freely assign the
            agreement in connection with a reorganization, merger, demerger or sale of
            assets. If any provision is held invalid, this does not affect the validity
            of the remaining provisions; the invalid provision shall be replaced by a
            valid one with the closest economic effect. Failure to enforce a right is
            not a waiver of it. These Terms, together with the approved application,
            constitute the entire agreement between the parties regarding the Program
            and supersede all prior understandings.
          </p>

          <h3 id="law">21. Governing law &amp; jurisdiction</h3>
          <p>
            These Terms are governed by the laws of the <strong>Republic of
            Bulgaria</strong>, excluding its conflict of laws rules. All disputes shall
            be referred to the competent courts of the <strong>City of Sofia</strong>,
            without prejudice to mandatory consumer protection rules. EU consumers may
            also use the European Commission's Online Dispute Resolution platform:{" "}
            <a href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noopener noreferrer">
              ec.europa.eu/consumers/odr
            </a>.
          </p>

          <h3 id="contact">22. Contact</h3>
          <p>
            For all Program and privacy enquiries, please use the same contact details as
            the rest of the site:
          </p>
          <ul>
            <li><strong>Email:</strong> <a href={`mailto:${PROGRAM_EMAIL}`}>{PROGRAM_EMAIL}</a></li>
            <li><strong>Phone:</strong> <a href={`tel:${PROGRAM_PHONE.replace(/\s+/g, "")}`}>{PROGRAM_PHONE}</a></li>
            <li><strong>Address:</strong> {PROGRAM_ADDRESS}</li>
            <li><strong>Hours:</strong> {PROGRAM_HOURS}</li>
          </ul>

          <hr className="my-12 border-border" />
          <p className="text-xs text-slate-500">
            This document has been prepared with the care of a diligent trader and aims
            to provide transparency and protect the interests of both FIXHEN and the
            Partner. It does not constitute individual legal advice; where in doubt as
            to the tax or regulatory regime in a specific jurisdiction, we recommend
            consulting a qualified adviser.
          </p>
        </article>

        {/* Bottom actions */}
        <div className="mt-12 flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-border bg-white p-6">
          <div className="text-sm text-slate-600">
            By submitting an application you confirm that you have read and accept these
            Terms &amp; Conditions <strong>v{s.terms_version}</strong>.
          </div>
          <div className="flex gap-3">
            <Link
              to="/referral/terms"
              className="inline-flex items-center gap-1 rounded-full border border-border bg-white px-4 py-2 text-sm font-semibold text-ink hover:bg-slate-50"
            >
              Open standalone
            </Link>
            <Link
              to="/referral/apply"
              className="inline-flex items-center gap-2 rounded-full bg-ink px-5 py-2.5 text-sm font-semibold text-paper hover:opacity-90"
            >
              Apply now <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
